import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { CONTACT_EMAIL, CONTACT_MAILTO } from "../lib/contact";
import "./auth/auth.css";
import "./privacy.css";

/**
 * Så hanterar jag dina uppgifter: the portal's privacy information, open to anybody.
 *
 * Decided by Mattej 2026-10-06: the information becomes its own page, openable without signing in,
 * linked from the sign-in page and from Inställningar. The text is the client-facing half of
 * `PetrovicSystems/Legal/privacy-notice.md`, everything under its double rule, sections 1 to
 * 10. The internal half above the rule (what plan.md asks of it, and the checklist before it was
 * published) is not here and must not be: `tests/privacy-route.test.tsx` fails on any of it.
 *
 * **The words live in this file, not in the markdown.** The page is the published text, so it is
 * compiled into the bundle and nothing is fetched to show it. Changing one means changing the
 * other in the same step; the markdown says so too. The source once paused on a dash in eight places
 * and this page made each of those two sentences, because no visible text in the portal pauses on a
 * dash; the source has said the same since, so the two read alike word for word. Mirrored again
 * 2026-10-09: punkt 3's Mercator purpose, punkt 6's "Min dator" and the date. Mirrored again
 * 2026-10-23 (build round 46b; Mattej's yes, decision 0610): punkt 4's row "Månadsrapporter" and the date.
 *
 * **Outside the session guard and outside the shell, and it never asks who is reading.** It is the
 * information a person is owed before they sign in as much as after, so it reads no session, calls
 * neither Supabase nor the Worker, and renders the same for both. It stands in the frame of the
 * other pages outside the shell, the wordmark and one box, made wider because it is a document with
 * three tables rather than a form.
 */
export function PrivacyRoute() {
  return (
    <div className="k-auth k-privacy">
      <header className="k-auth-brand">
        <p className="k-wordmark">
          <b>PETROVIC SYSTEMS</b>
          <span>Kundportal</span>
        </p>
      </header>
      <main className="k-auth-main">
        <article className="panel k-auth-card k-privacy-card" aria-labelledby="privacy-title">
          <div className="panel-body">
            <h1 id="privacy-title" className="k-privacy-title">
              Så hanterar jag dina uppgifter i kundportalen
            </h1>
            <p>
              Det här gäller uppgifter jag behandlar för egen räkning i portalen. Filer du laddar upp är
              något annat, och punkt 9 säger vad.
            </p>

            <Section number={1} title="Vem ansvarar">
              <p>Mattej Petrovic. Jag är personuppgiftsansvarig för det som står här.</p>
              <p>
                Har du en fråga, vill se vad jag har om dig, eller vill att något rättas eller raderas:
                hör av dig på{" "}
                <a href={CONTACT_MAILTO} data-testid="privacy-contact">
                  {CONTACT_EMAIL}
                </a>
                . Du får svar av mig, inte av ett formulär.
              </p>
              <p>Är du inte nöjd med hur jag hanterar det kan du vända dig till Integritetsskyddsmyndigheten, IMY.</p>
            </Section>

            <Section number={2} title="Vilka uppgifter portalen har om dig">
              <Table
                head={["Vad", "Vad det är"]}
                rows={[
                  [
                    "Konto",
                    "Namn, mejladress, telefonnummer, din roll hos kunden, ditt lösenord i krypterad form och din tvåfaktorsinställning"
                  ],
                  ["Det du skriver", "Chattmeddelanden mellan dig och mig, ärenden du skapar, kommentarer, statushistorik"],
                  ["Arbetshistorik", "Vad som beställts, när, och vad resultatet blev"],
                  ["Tekniska säkerhetsloggar", "IP-adress, webbläsare, tidpunkt och vad som gjordes"],
                  ["Revisionslogg", "Vem som gjorde vad i portalen och när"],
                  [
                    "Notiser",
                    "Mejladressen utskick går till, och din push-prenumeration om du slagit på notiser i webbläsaren"
                  ],
                  ["Engångshemligheter", "När jag behöver skicka dig ett lösenord eller en nyckel säkert"],
                  [
                    "Arbetsmängdspost",
                    "Se punkt 3. Den har ett eget stycke för att den lever längst av det som rör dig"
                  ]
                ]}
              />
              <p>
                Jag samlar inte in något annat. Ingen profilering, ingen annonsering, inget automatiserat
                beslut som får rättslig verkan för dig.
              </p>
            </Section>

            <Section number={3} title="Varför, och med vilket stöd">
              <p>
                <strong>För att portalen ska fungera för dig.</strong> Konto, chatt, ärenden, notiser. Är
                du enskild firma och själv kund vilar det på vårt avtal. Arbetar du hos ett kundföretag
                vilar det på mitt berättigade intresse av att kunna leverera det företaget beställt. Jag
                måste kunna nå den som faktiskt beställer, och det går inte utan ett konto.
              </p>
              <p>
                <strong>För säkerheten.</strong> Säkerhetsloggar och revisionslogg vilar på mitt
                berättigade intresse av att kunna se om något gått fel, om någon tagit sig in, och vem
                som gjorde vad. Utan dem kan jag inte utreda en incident, och då kan jag inte heller
                berätta för dig att du drabbats.
              </p>
              <p>
                <strong>För att kunna visa vad vi kom överens om.</strong> Avtal, godkännanden och besked
                jag skickat vilar på mitt berättigade intresse av att kunna försvara mig mot ett krav.
                Punkt 4 säger hur länge.
              </p>
              <p>
                <strong>För bokföringen.</strong> Fakturaunderlag vilar på rättslig förpliktelse.
                Bokföringslagen bestämmer tiden, inte jag.
              </p>
              <p>
                <strong>För notiser i webbläsaren.</strong> Push vilar på ditt samtycke. Du slår på det
                själv och du kan stänga av det när du vill, i portalen eller i webbläsarens
                inställningar.
              </p>
              {/*
                Mercator's purpose is three siblings rather than one wrapper, because the section lays
                its children out on its own grid and a wrapper would lose that spacing. Each carries
                its own test id (2026-10-09, after the template's punkt 3 changed for round B1).
              */}
              <p data-testid="privacy-mercator">
                <strong>För att jag ska veta vem som redan är kund, fakturera rätt och skriva
                månadsrapporten.</strong>{" "}
                Jag har ett eget program på min dator som håller ordning på förfrågningar, försäljning
                och fakturering. Det frågar portalen på tre sätt:
              </p>
              <ul className="k-privacy-list" data-testid="privacy-mercator-reads">
                <li>
                  Det skickar mejladresser till portalen: från förfrågningar via min webbplats, från
                  säljsamtal och från mina egna kontakter. Portalen svarar bara för en adress som hör
                  till en kontaktperson hos en kund, aktiv eller nyss avslutad. Svaret säger vilken kund
                  det är (portalens kund-id, företagsnamnet och organisationsnumret, eller för en enskild
                  firma bara att det är en enskild firma), uppdraget, om förvaltningen pågår och när den
                  i så fall upphör, förvaltningsnivån, det avtalade priset per månad och periodens datum.
                  En adress som inte hör till en kund får inget svar alls. Portalen sparar inga av
                  adresserna den får.
                </li>
                <li>Det hämtar listan över kunder som har Förvaltning och utveckling en viss månad.</li>
                <li>
                  Det hämtar en kunds månad till månadsrapporten: rubrikerna på ärendena, öppna fel,
                  besök och drifttid.
                </li>
              </ul>
              <p data-testid="privacy-mercator-basis">
                Portalen skickar aldrig ett personnummer. För en enskild firma skickas inget nummer alls.
                Svaren hamnar på min egen dator och ingen annanstans. Ändamålen är mina egna: jag ska se
                vilka förfrågningar som blev kunder, inte höra av mig om försäljning till någon som
                redan är kund, fakturera det belopp vi kommit överens om och skriva kundens
                månadsrapport. Grunden är berättigat intresse.
              </p>
              <p>
                <strong>Arbetsmängdsposten</strong>, som är den enda posten som lever kvar efter att allt
                annat om dig raderats, förtjänar hela sitt eget stycke. Den innehåller avslutningsmånad,
                produkttyp, ärendetyp, omfattningsresultat och uppskattat antal hela minuter. Inget namn,
                ingen fritext, inga ärendedetaljer, ingen tidsstämpel som pekar ut ett tillfälle.
                Ändamålet är mitt eget: jag behöver veta vad olika sorters arbete faktiskt kostar i tid,
                annars kan jag varken prissätta eller planera. Grunden är berättigat intresse.{" "}
                <strong>Är kunden en juridisk person står bara kundens namn kvar och inget om dig.</strong>{" "}
                Är kunden en enskild firma eller på annat sätt en fysisk person byts kopplingen mot ett
                internt pseudonym-id. Jag kallar den inte anonym, för den är det inte, och att kalla den
                det vore ett sätt att slippa skriva det här stycket.
              </p>
            </Section>

            <Section number={4} title="Hur länge">
              <Table
                head={["Vad", "Hur länge"]}
                rows={[
                  [
                    "Konto, chatt, ärenden, historik",
                    "Så länge förvaltningen pågår, och tolv månader efter att den upphört"
                  ],
                  [
                    "Efter tolv månader",
                    "Fritext, ärendedetaljer, ärende-id, användarkopplingar, exakta tidpunkter och varje fält som pekar ut vem som skrev eller gjorde något raderas"
                  ],
                  [
                    "Månadsrapporter",
                    "Så länge förvaltningen pågår (de tolv senaste). Raderas 30 dagar efter att förvaltningen upphört, när tiden för att hämta materialet har gått ut"
                  ],
                  [
                    "Arbetsmängdsposten",
                    "Fem år från beställningens avslutningsmånad, i den avskalade form punkt 3 beskriver"
                  ],
                  ["Tekniska säkerhetsloggar", "90 dagar"],
                  ["Revisionslogg, vanliga händelser", "Tolv månader"],
                  [
                    "Revisionslogg, det som bevisar avtal, godkännande, överlämning eller avslut",
                    "Tio år"
                  ],
                  ["Avtal och godkännanden", "Tio år"],
                  ["Bokföringsmaterial", "Sju år"],
                  [
                    "Engångshemligheter",
                    "Raderas efter första visningen, eller efter sju dagar om de aldrig öppnas"
                  ],
                  ["Nedladdningslänkar", "Slutar gälla efter 45 sekunder och raderas kort därefter"],
                  ["Säkerhetskopior", "35 dagar. Se punkt 7"]
                ]}
              />
              <p>
                <strong>Tio år, och varför just det.</strong> Ett krav mellan företag kan väckas i tio år
                i Sverige. Sparar jag avtalsbevisen kortare än så är de borta innan tvisten de finns för
                kan uppstå. Den tiden gäller vad vi kom överens om. Den gäller inte ditt material och
                inte din chatt.
              </p>
              <p>
                <strong>Tolv månader är ett tak.</strong> Inget val du eller din arbetsgivare gör kan
                skjuta upp det. Ett val kan flytta raderingen närmare i tiden. Det kan aldrig flytta den
                längre bort.
              </p>
              <p>
                <strong>De första trettio dagarna efter slutdatumet</strong> har du läsbehörighet kvar och
                kan hämta ut material. Sedan stängs inloggningen.
              </p>
            </Section>

            <Section number={5} title="Vilka andra som rör uppgifterna">
              <Table
                head={["Leverantör", "Vad de gör", "Var"]}
                testId="privacy-suppliers"
                rows={[
                  ["Supabase", "Databas och inloggning", "EU, Tyskland (Frankfurt)"],
                  [
                    "Cloudflare",
                    "Kör portalen, lagrar filer, och kör Turnstile som skiljer människa från robot vid inloggning",
                    "EU. Lagringshinkarna är skapade med EU-jurisdiktion"
                  ],
                  ["Resend", "Portalens systemmejl", "EU, Irland"],
                  ["GitHub", "Kör den dagliga krypterade säkerhetskopian", "EU och USA"],
                  [
                    "Google Workspace",
                    "Förvarar den färdiga krypterade säkerhetskopian, och tar emot mejl du skickar direkt till mig",
                    "EU och USA"
                  ],
                  [
                    "Din webbläsares push-tjänst",
                    "Levererar notiser, om du slagit på dem. Apple, Google, Mozilla eller Microsoft beroende på webbläsare",
                    "Beror på leverantören"
                  ]
                ]}
              />
              <p>
                Jag har personuppgiftsbiträdesavtal med de fem första. Tillkommer en till säger jag till
                innan den tas i bruk.
              </p>
              <p>
                <strong>Push-tjänsten på sista raden är ett undantag och det ska stå här.</strong> Den
                står inte i mitt namn och jag har inget personuppgiftsbiträdesavtal med den. Jag kan inte
                heller välja vilken det blir: Safari på iPhone ger Apples tjänst, Chrome ger Googles,
                Firefox ger Mozillas, Edge på Windows ger Microsofts. Din webbläsare bestämmer, inte jag.
              </p>
              <p>
                Till den skickas två saker. En adress som din webbläsare har skapat åt just din enhet,
                och själva notisen krypterad med en nyckel som bara din enhet har. Push-tjänsten ser att
                något skickas till din enhet. Den kan inte läsa vad som står i notisen, och inget
                ärendenummer, namn eller text ur portalen går dit i klartext.
              </p>
              <p>
                Push är avstängt tills du slår på det själv i inställningarna, och du kan stänga av det
                igen när du vill. Notiser inne i portalen och på mejl fungerar utan. Slår du aldrig på
                push kontaktas ingen push-tjänst.
              </p>
              <p>
                <strong>Ingen av dem får uppgifterna för egen räkning.</strong> De behandlar dem för att
                driva sin tjänst åt mig, och ingenting säljs eller lämnas vidare till någon annan.
              </p>
              <p>
                <strong>GitHub, Google och USA.</strong> Säkerhetskopian skapas hos GitHub och förvaras
                hos Google, och båda kan behandla den i EU och USA. Filen krypteras innan den lämnar
                körningen och nyckeln finns varken hos GitHub eller Google. Det de förvarar är oläsbart
                för dem. Överföringen stöds av EU-US Data Privacy Framework och standardavtalsklausuler.
              </p>
              <p>
                <strong>Mejl du skickar direkt till mig</strong> hamnar däremot hos Google i läsbar form,
                i EU och USA. Vill du undvika det: skriv i portalen i stället. Där stannar uppgifterna
                hos leverantörerna ovan.
              </p>
            </Section>

            <Section number={6} title="Var uppgifterna finns">
              <p>Fyra ställen, och inte fler.</p>
              <p>
                <strong>Databasen</strong> hos Supabase i Tyskland (Frankfurt). All text: ditt konto, chatten,
                ärendena, loggarna.
              </p>
              <p>
                <strong>Fillagret</strong> hos Cloudflare, i EU-jurisdiktion. Filer som laddas upp, och
                avtalsbevis.
              </p>
              <p>
                <strong>Säkerhetskopiorna</strong>, krypterade, hos Google. Punkt 7 säger resten.
              </p>
              <p>
                <strong>Min dator</strong>, för svaren i punkt 3: det som räknas upp där om kunden,
                uppdraget, förvaltningen och månaden, och inget annat från portalen. Aldrig ett
                personnummer.
              </p>
            </Section>

            <Section number={7} title="Säkerhetskopior">
              <p>
                <strong>Säkerhetskopior finns, och det ska du veta.</strong>
              </p>
              <p>
                Varje natt tas en krypterad kopia av databasen och av filerna. Kopiorna roteras: sju
                dygnskopior och fyra veckokopior, alltså <strong>högst 35 dagar bakåt</strong>. Avtal och
                godkännanden har en egen uppsättning kopior med ett längre fönster, eftersom de själva
                sparas i tio år.
              </p>
              <p>
                <strong>Radering är därför inte omedelbart borta.</strong> Raderas något ur portalen kan
                det ligga kvar i en krypterad kopia i högst trettiofem dagar, och försvinner när den
                kopian roteras ut. Det är inte ett kryphål. Det är hur säkerhetskopior fungerar, och
                därför står det här i stället för att stå ingenstans.
              </p>
              <p>
                Kopiorna krypteras innan de lämnar körningen. Nyckeln förvaras offline och finns inte hos
                någon av leverantörerna i punkt 5.
              </p>
            </Section>

            <Section number={8} title="Dina rättigheter">
              <p>Du har rätt att:</p>
              <ul className="k-privacy-list">
                <li>
                  <strong>få veta vad jag har om dig</strong>, och få en kopia
                </li>
                <li>
                  <strong>få fel rättade</strong>
                </li>
                <li>
                  <strong>få uppgifter raderade</strong>, när jag inte har ett skäl som väger tyngre.
                  Bokföring och avtalsbevis är sådana skäl, och punkt 4 säger hur länge de gäller
                </li>
                <li>
                  <strong>invända</strong> mot behandling som vilar på mitt berättigade intresse, och då
                  prövar jag om mitt skäl faktiskt väger tyngre än ditt
                </li>
                <li>
                  <strong>begära att behandlingen begränsas</strong> medan en sådan fråga utreds
                </li>
                <li>
                  <strong>ta med dig</strong> de uppgifter du själv lämnat, i ett läsbart format
                </li>
                <li>
                  <strong>ta tillbaka ditt samtycke</strong> till notiser, när som helst
                </li>
              </ul>
              <p>
                Hör av dig till adressen i punkt 1. Jag svarar inom en månad, och kostar det ingenting
                gör jag det fortare än så.
              </p>
              <p>
                <strong>Ett svar som säger nej säger också varför.</strong> Får du inte något raderat får
                du veta vilken regel som hindrar det och vilket datum den upphör.
              </p>
            </Section>

            <Section number={9} title="Filer du laddar upp är något annat">
              <p>
                Laddar du upp en fil åt kundföretagets räkning kan den innehålla uppgifter om{" "}
                <strong>företagets egna kunder</strong>. Där är jag inte ansvarig. Där är jag biträde,
                och företaget bestämmer.
              </p>
              <p>
                Det regleras i personuppgiftsbiträdesavtalet mellan mig och företaget, inte här. Vill du
                veta vad som gäller för en uppladdad fil, fråga den hos er som skrivit under det avtalet.
              </p>
              <p>
                Gränsen går alltså inte mellan olika sorters uppgifter utan mellan{" "}
                <strong>vem som bestämmer över dem</strong>. Kontot, chatten och loggarna bestämmer jag
                över. Filerna bestämmer företaget över.
              </p>
            </Section>

            <Section number={10} title="Ändringar">
              <p>Ändras något av det här får du beskedet i portalen och per mejl innan ändringen träder i kraft.</p>
              <p className="k-privacy-date" data-testid="privacy-updated">
                Senast ändrad: 23 oktober 2026
              </p>
            </Section>
          </div>
        </article>

        {/*
          The way on, the same one the pages for a wrong address and a crash give. `/overview` is the
          portal for a signed-in reader, and `RequireSession` turns it into the sign-in page for
          anybody else, so the page can offer one link without knowing which of the two is reading.
        */}
        <div className="k-auth-links k-privacy-links">
          <Link to="/overview">Till portalen</Link>
        </div>
      </main>
    </div>
  );
}

/** One numbered section, named by its own heading so a screen reader can jump between the ten. */
function Section({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  const id = `privacy-${number}`;

  return (
    <section className="k-privacy-section" aria-labelledby={id}>
      <h2 id={id}>
        {number}. {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * A table with real header cells: one per column, and the first cell of every row as that row's
 * header, because "Konto" or "Supabase" is what the row is about. On a phone the rows stack (see
 * `privacy.css`), and a third column carries its header's name in front of it, since the head row
 * is then out of sight.
 */
function Table({ head, rows, testId }: { head: string[]; rows: string[][]; testId?: string }) {
  return (
    <table className="k-privacy-table" data-testid={testId}>
      <thead>
        <tr>
          {head.map((cell) => (
            <th key={cell} scope="col">
              {cell}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(([lead, ...rest]) => (
          <tr key={lead}>
            <th scope="row">{lead}</th>
            {rest.map((cell, index) => (
              <td key={index} data-label={index > 0 ? head[index + 1] : undefined}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
